/**
 * Profile photo and display-name editor, including pseudonym validation and
 * the first-login setup screen.
 *
 * Exports the `photoandname` `ProfileModule`, which `profile-module.ts` wires
 * onto Profile's `profileModules` slot unconditionally. The view shows the
 * user's photo and name; the edit pane lets users keep their login-provider
 * name or pick a custom pseudonym, and lets admins (or anyone with a provider
 * photo) swap between letter avatar, provider photo, and a custom photo URL.
 * Name validation (format, uniqueness, prohibited/real-name content) runs
 * server-side inside `profiles.update`; `nameErrorsForUpdateFailure` maps its
 * typed errors onto the editor's banners.
 *
 * `FirstLoginSetup` is a standalone screen (not a profile module) that wraps
 * the same editor with a `<WithEditableFields>` provider for use during
 * onboarding, plus a consent checkbox gate before submit.
 */

import { ORPCError } from '@orpc/client';
import { MANAGE_PROFILE_DETAILS_CAPABILITY } from '@repo/shared/roles-capabilities';
import { useTheme } from '@repo/theme';
import { useLegalDocLinks, useShowCollaborationNote, useSiloName } from 'component/admin';
import { Banner } from 'component/banner';
import { AdminBanner } from 'component/banner/admin-banner';
import { HorizBox, Pad, PadBox } from 'component/basics';
import { CTAButton } from 'component/button';
import { RadioGroup, RadioOption, ToggleSwitch } from 'component/form';
import { Checkbox } from 'component/form/checkbox';
import { FaceImage, FaceSelect, LetterFace, ProfilePhoto } from 'component/people';
import { RichText } from 'component/richtext';
import { Heading, Paragraph, TextField, UtilityText } from 'component/text';
import { useTranslate } from 'component/translation';
import { profileHasOverride, useProfile } from 'data/profile';
import { useEffect, useRef, useState } from 'react';
import { type TextInput, View } from 'react-native';
import {
    defaultCustomNameValidation,
    emptyCustomNameError,
    type ProfileConfig,
    type ProfileModule,
    useEditableField,
    useEditErrors,
    WithEditableFields,
} from 'structure/profile';
import { Catcher } from 'system/catcher';
import { showApiErrorToast } from 'system/orpc-errors';
import { useHasCapability, useIsAdmin } from 'util/admin';
import { useSession } from 'util/auth-client';
import { useDatastore, useInstanceKey, usePersonaKey, useSiloKey } from 'util/datastore';
import { useConfig } from 'util/features-hooks';
import { makeRelativeUrlForInstance } from 'util/navigate';
import { t } from 'util/stdtypes';

export const photoAndNameProfileModule: ProfileModule = {
    label: t('profile.module_photo_and_name', 'photo and name'),
    resolveLabel: ({ canEditProfilePhoto }) =>
        canEditProfilePhoto === false ? t('profile.module_name', 'name') : t('profile.module_photo_and_name', 'photo and name'),
    key: 'photoandname',
    view: ViewPhotoAndName,
    edit: EditPhotoAndName,
    mapSaveError: nameErrorsForUpdateFailure,
};

function ViewPhotoAndName() {
    const personaKey = useInstanceKey();
    const { profile } = useProfile(personaKey);

    return (
        <View>
            <HorizBox center>
                <ProfilePhoto profile={profile} type='huge' />
                <Pad />
                <Heading text={profile?.name ?? 'Unknown User'} type='large' weight='medium' />
            </HorizBox>
        </View>
    );
}

function OverrideBanner({ canManageProfileDetails, overriden }: { canManageProfileDetails?: boolean | null; overriden?: boolean | null }) {
    const translate = useTranslate();
    const siloKey = useSiloKey();
    const link = makeRelativeUrlForInstance({
        structureKey: 'editorialdash',
        instanceKey: 'one',
        screenKey: 'admins',
        siloKey,
    });

    if (canManageProfileDetails) {
        const editedByAdminText = translate(
            t('profile.warn_overriden', 'Your information has been edited by an Admin in the [Editorial Dashboard]({link}).'),
            { link },
        );
        const editorialInfoText = translate(
            t(
                'profile.editorial_info',
                'As an Admin user, you can access more options for your profile in the [Editorial Dashboard]({link}).',
            ),
            { link },
        );
        const text = overriden ? editedByAdminText : editorialInfoText;
        return (
            <>
                <AdminBanner text={text} />
                <Pad size={24} />
            </>
        );
    }
    if (overriden) {
        return (
            <>
                <Banner>
                    <UtilityText label={t('profile.public_overriden', 'Your information has been edited by an Admin')} />
                </Banner>
                <Pad size={24} />
            </>
        );
    }
    return null;
}

function EditPhotoAndName() {
    const session = useSession();
    const user = session.data?.user;
    const canManageProfileDetails = useHasCapability(MANAGE_PROFILE_DETAILS_CAPABILITY);
    const ref = useRef<HTMLElement>(null);
    const { canEditProfilePhoto } = useConfig<ProfileConfig>();
    const canEditPhoto = canEditProfilePhoto !== false && (canManageProfileDetails || !!user?.image);
    const personaKey = useInstanceKey();
    const { colors } = useTheme();

    const { profile } = useProfile(personaKey);
    const overriden = profileHasOverride(profile);
    const [nameMode, setNameMode] = useState(profile?.customName ? 'custom' : 'full');
    const [, setCustomName] = useEditableField('customName', profile?.customName ?? null);

    function onChangeNameMode(mode: string): void {
        setNameMode(mode);
        setCustomName(mode === 'custom' ? '' : null);
    }

    useEffect(() => {
        ref.current?.focus();
    }, []);

    return (
        <View>
            <UtilityText
                color={colors.colorTextSecondary}
                label={t('profile.helper_text', 'Choose a name and photo that appears when you post in the community.')}
            />
            <Pad size={24} />
            {profile ? <OverrideBanner canManageProfileDetails={canManageProfileDetails} overriden={overriden} /> : null}
            <UtilityText label={t('profile.name_label', 'Profile name')} tabIndex='-1' textRef={ref} weight='medium' />
            <Pad size={8} />
            <RadioGroup onChange={onChangeNameMode} value={nameMode}>
                <RadioOption disabled={overriden} radioKey='full' text={user?.name ?? 'Unknown User'} />
                <RadioOption disabled={overriden} label={t('profile.add_custom_name', 'Add a custom name')} radioKey='custom' />
            </RadioGroup>
            {nameMode === 'custom' && (
                <Catcher>
                    <PseudonymEditor />
                </Catcher>
            )}
            {canEditPhoto && (
                <PadBox top={32}>
                    <UtilityText label={t('profile.photo_label', 'Your profile photo')} weight='medium' />
                    <Pad size={12} />
                    <Catcher>
                        <ProfilePhotoEditor disabled={overriden} />
                    </Catcher>
                </PadBox>
            )}
            <Pad size={24} />
        </View>
    );
}

// eslint-disable-next-line max-lines-per-function
function ProfilePhotoEditor({ disabled }: { disabled?: boolean }) {
    const session = useSession();
    const personaKey = usePersonaKey();
    const instanceKey = useInstanceKey();
    const { profile } = useProfile(instanceKey);
    const user = session.data?.user;
    const providerPhoto = user?.image;
    const isAdmin = useIsAdmin();

    const [photoHidden, setPhotoHidden] = useEditableField('photoHidden', profile?.photoHidden ?? false);
    const [customPhotoUrl, setCustomPhotoUrl] = useEditableField('customPhotoUrl', profile?.customPhotoUrl ?? null);
    const [customName] = useEditableField('customName', profile?.customName ?? null);

    const [showUrl, setShowUrl] = useState(!!customPhotoUrl);
    const [customUrlInput, setCustomUrlInput] = useState(customPhotoUrl ?? '');

    const letterName = customName ?? user?.name ?? '';
    const shownPhoto = customPhotoUrl ?? providerPhoto;
    const hasPhoto = !!shownPhoto;

    function onSelectLetter() {
        setPhotoHidden(true);
    }

    function onSelectPhoto() {
        setPhotoHidden(false);
    }

    function onPhotoUrlChange(newUrl: string) {
        if (!newUrl && !providerPhoto) {
            onSelectLetter();
        }
        setCustomUrlInput(newUrl);
        setCustomPhotoUrl(newUrl || null);
        if (newUrl) {
            setPhotoHidden(false);
        }
    }

    function onToggleCustomPhoto(enabled: boolean) {
        setShowUrl(enabled);
        if (enabled) {
            setPhotoHidden(false);
        } else {
            setCustomPhotoUrl(null);
        }
    }

    return (
        <View>
            {isAdmin && (
                <ToggleSwitch
                    disabled={disabled}
                    label={t('profile.custom_photo_label', 'Custom profile photo')}
                    onChange={onToggleCustomPhoto}
                    spread
                    value={showUrl}
                />
            )}
            {showUrl && (
                <TextField
                    ariaLabel={t('profile.photo_url_aria', 'Photo URL')}
                    multiline={false}
                    onChange={onPhotoUrlChange}
                    placeholderLabel={t('profile.photo_url_placeholder', 'Photo URL')}
                    testID='photo-url'
                    value={customUrlInput}
                />
            )}
            {isAdmin && <Pad size={12} />}
            {!showUrl && (
                <HorizBox>
                    {hasPhoto && (
                        <>
                            <FaceSelect
                                ariaLabel='Photo'
                                disabled={disabled}
                                onSelect={onSelectPhoto}
                                selected={!photoHidden}
                                testID='photo'
                            >
                                <FaceImage photoUrl={shownPhoto ?? ''} type='large' />
                            </FaceSelect>
                            <Pad size={16} />
                        </>
                    )}
                    <FaceSelect
                        ariaLabel='Letter'
                        disabled={disabled}
                        onSelect={onSelectLetter}
                        selected={photoHidden || !hasPhoto}
                        testID='letter'
                    >
                        <LetterFace name={letterName} type='large' userId={personaKey} />
                    </FaceSelect>
                </HorizBox>
            )}
        </View>
    );
}

type ProfileErrorBannerProps = {
    errors: any;
};

function ProfileErrorBanner({ errors }: ProfileErrorBannerProps) {
    // `customNameValidation` is a ProfileConfig slot, but FirstLoginSetup
    // renders this under the login structure (no ProfileConfig slots) — fall
    // back to the default so the error renders instead of crashing.
    const { customNameValidation = defaultCustomNameValidation } = useConfig<ProfileConfig>();
    const { colors } = useTheme();
    if (errors.nameTaken) {
        return <UtilityText color={colors.colorTextWarning} label={t('profile.error_name_taken', 'This custom name is already in use.')} />;
    }
    if (errors.nameEmpty) {
        return <UtilityText color={colors.colorTextWarning} label={t('profile.error_name_empty', 'Custom names cannot be empty.')} />;
    }
    if (errors.nameInvalid) {
        return <UtilityText color={colors.colorTextWarning} label={customNameValidation.errorMessage} />;
    }
    if (errors.nameViolates) {
        return (
            <Banner color={colors.colorTagHighlight}>
                <UtilityText
                    color={colors.colorTextOnTagLight}
                    label={t('profile.error_name_violates', 'This custom name is incompatible with our Community Guidelines.')}
                />
            </Banner>
        );
    }
    if (errors.nameLooksReal) {
        return (
            <Banner color={colors.colorTagHighlight}>
                <UtilityText
                    color={colors.colorTextOnTagLight}
                    label={t('profile.error_name_looks_real', 'Custom names are not allowed to look like real names.')}
                />
            </Banner>
        );
    }
}

function ProfileNameGuidelines() {
    const { colors } = useTheme();
    return (
        <UtilityText
            color={colors.colorTextSecondary}
            label={t('profile.name_change_allowed_characters', 'Custom names can only include lowercase letters and numbers.')}
            type='small'
        />
    );
}

function hasNonModerationErrors(errors: Record<string, any>): boolean {
    // nameLooksReal and nameViolates aren't highlighted
    // like errors as they are moderation results
    return Object.keys(errors).some((key) => !['nameLooksReal', 'nameViolates'].includes(key));
}

function PseudonymEditor() {
    const personaKey = useInstanceKey();
    const { profile } = useProfile(personaKey);
    const [customName, setCustomName] = useEditableField('customName', profile?.customName ?? '');
    const errors = useEditErrors();
    const hasNonModerationError = hasNonModerationErrors(errors);
    const hasError = Object.keys(errors).length !== 0;
    const nameInputRef = useRef<TextInput>(null);
    useEffect(() => {
        nameInputRef.current?.focus();
    }, [errors]);
    return (
        <View>
            <TextField
                error={hasNonModerationError}
                inputForwardedRef={nameInputRef}
                multiline={false}
                onChange={setCustomName}
                placeholderLabel={t('profile.username_placeholder', 'user123')}
                testID='pseudonym'
                value={customName ?? ''}
            />
            <Pad size={12} />
            <View aria-live='assertive'>{hasError ? <ProfileErrorBanner errors={errors} /> : <ProfileNameGuidelines />}</View>
        </View>
    );
}

/**
 * Maps a `profiles.update` failure onto the error keys `ProfileErrorBanner`
 * renders. Name validation lives entirely in the server's `validateNameAsync`
 * (via `profiles.update`), so its typed errors are the sole source of the
 * editor's name banners — a too-short, malformed, taken, prohibited, or
 * real-looking name each surfaces its matching banner here. Returns `null` for
 * anything else (a network failure, an unexpected server error), which isn't
 * one of this screen's known/expected failures — the caller falls back to the
 * generic `showApiErrorToast`.
 */
function nameErrorsForUpdateFailure(error: unknown): Record<string, boolean> | null {
    if (!(error instanceof ORPCError)) return null;
    switch (error.code) {
        case 'NAME_TAKEN':
            return { nameTaken: true };
        case 'NAME_PROHIBITED':
            return { nameViolates: true };
        case 'NAME_NOT_REAL':
            return { nameLooksReal: true };
        case 'INVALID_NAME':
            return { nameInvalid: true };
        case 'EMPTY_NAME':
            return { nameEmpty: true };
        default:
            return null;
    }
}

export type FirstLoginSetupProps = {
    onFieldsChosen: ({ updates }: { updates: Record<string, any> }) => Promise<void>;
};

export function FirstLoginSetup({ onFieldsChosen }: FirstLoginSetupProps) {
    const datastore = useDatastore();
    const psmName = useSiloName();
    const showCollaborationNote = useShowCollaborationNote() ?? false;
    const { colors } = useTheme();

    // A new user defaults to showing their provider photo (the save copies it
    // in); the custom name and photo start untouched.
    const [updates, setUpdates] = useState<Record<string, any>>({ photoHidden: false });
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const [inProgress, setInProgress] = useState(false);
    const [agreedConsent, setAgreedConsent] = useState(false);

    async function onFinish() {
        setInProgress(true);
        setErrors({});
        const emptyNameError = emptyCustomNameError(updates);
        if (emptyNameError) {
            setErrors(emptyNameError);
            setInProgress(false);
            return;
        }

        try {
            await onFieldsChosen({ updates });
        } catch (e) {
            const nameErrors = nameErrorsForUpdateFailure(e);
            if (nameErrors) {
                setErrors(nameErrors);
            } else {
                showApiErrorToast({ datastore, error: e });
            }
            setInProgress(false);
        }
    }

    return (
        <View>
            <PadBox horiz={20} vert={20}>
                <Heading label={t('profile.setup_title', 'Set up your profile')} type='large' weight='medium' />
                <Pad size={8} />
                <WithEditableFields errors={errors} setUpdates={setUpdates} updates={updates}>
                    <EditPhotoAndName />
                </WithEditableFields>
                <Pad size={32} />
                <ConsentAgreement onChange={setAgreedConsent} value={agreedConsent} />
                <Pad size={32} />
                <CTAButton
                    disabled={inProgress || !agreedConsent}
                    label={inProgress ? t('profile.please_wait', 'Please Wait...') : t('profile.finish_button', 'Finish')}
                    onPress={onFinish}
                />
                <Pad size={16} />
                {psmName && showCollaborationNote && (
                    <Paragraph
                        color={colors.colorTextSecondary}
                        formatParams={{ psmName }}
                        label={t(
                            'profile.collaboration_note',
                            'This discussion space is hosted by New_ Public, in collaboration with {psmName}.',
                        )}
                        type='small'
                    />
                )}
                <Pad size={16} />
                <Paragraph
                    color={colors.colorTextSecondary}
                    label={t(
                        'profile.beta_feedback_note',
                        'By participating in our beta, you may be invited to submit feedback on your experience using this product.',
                    )}
                    type='small'
                />
            </PadBox>
        </View>
    );
}

type ConsentAgreementProps = {
    value: boolean;
    onChange: (value: boolean) => void;
};

function ConsentAgreement({ value, onChange }: ConsentAgreementProps) {
    const siloKey = useSiloKey();
    const { communityGuidelinesLink, privacyNoticeLink, termsOfServiceLink } = useLegalDocLinks(siloKey);
    const { colors } = useTheme();

    return (
        <HorizBox center>
            <Checkbox
                ariaLabel={t('profile.consent_aria', 'I agree to the Community Guidelines, Privacy Notice, and Terms of Service.')}
                onChange={onChange}
                testID='consent-agreement'
                value={value}
            />
            <Pad size={12} />
            <RichText
                color={colors.colorTextSecondary}
                formatParams={{ communityGuidelinesLink, privacyNoticeLink, termsOfServiceLink }}
                label={t(
                    'profile.consent_text',
                    'I agree to the [Community Guidelines]({communityGuidelinesLink}), [Privacy Notice]({privacyNoticeLink}), and [Terms of Service]({termsOfServiceLink}).',
                )}
            />
        </HorizBox>
    );
}
